import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  Plus,
  Upload,
  Trash2,
  Edit3,
  Search,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  AlertTriangle,
  UserPlus,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  HelpCircle,
  RotateCcw
} from 'lucide-react';

const recipientSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  city: z.string().optional(),
  tags: z.string().optional(),
  status: z.enum(['SUBSCRIBED', 'UNSUBSCRIBED', 'BOUNCED', 'COMPLAINED', 'INVALID']).default('SUBSCRIBED'),
  listIds: z.array(z.string()).optional()
});

type RecipientFormValues = z.infer<typeof recipientSchema>;

export const Recipients: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'recipients' | 'validation'>(
    (searchParams.get('tab') as 'recipients' | 'validation') || 'recipients'
  );
  const setTab = (tab: 'recipients' | 'validation') => {
    setActiveTab(tab);
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', tab); return next; }, { replace: true });
  };

  // Page states
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [listFilter, setListFilter] = useState('');
  const [sortBy, setSortBy] = useState('_id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Validation report tab state
  const [vrPage, setVrPage] = useState(1);
  const [vrPageInput, setVrPageInput] = useState('1');
  const [vrSearch, setVrSearch] = useState('');
  const [vrStatus, setVrStatus] = useState('');
  
  // Selected checks for bulk delete
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  
  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // File import state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importListId, setImportListId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [importJobStatus, setImportJobStatus] = useState<any>(null);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const openConfirm = (title: string, message: string, onConfirm: () => void) =>
    setConfirmDialog({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmDialog(d => ({ ...d, open: false }));

  // Quick status change (inline badge dropdown)
  const [statusMenuId, setStatusMenuId] = useState<string | null>(null);
  const [quickStatusLoading, setQuickStatusLoading] = useState<string | null>(null);

  // Deliverability check state
  const [isDelivModalOpen, setIsDelivModalOpen] = useState(false);
  const [delivScope, setDelivScope] = useState<'subscribed' | 'selected' | 'limit'>('subscribed');
  const [delivLimit, setDelivLimit] = useState('10000');
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [delivResults, setDelivResults] = useState<null | {
    checked: number;
    valid: number;
    invalid: Array<{ id: string; email: string; domain: string }>;
    unknown: Array<{ id: string; email: string; domain: string }>;
    suppressedBounce: Array<{ id: string; email: string; suppressedAt: string }>;
    suppressedComplaint: Array<{ id: string; email: string; suppressedAt: string }>;
  }>(null);
  const [applyingResults, setApplyingResults] = useState(false);

  // Fetch recipients
  const { data: recipientsData, isLoading, isPreviousData } = useQuery({
    queryKey: ['recipients', page, debouncedSearch, statusFilter, tagFilter, listFilter, sortBy, sortDir],
    queryFn: async () => {
      const res = await apiClient.get('/recipients', {
        params: {
          page,
          limit:   50,
          search:  debouncedSearch || undefined,
          status:  statusFilter || undefined,
          tag:     tagFilter    || undefined,
          listId:  listFilter   || undefined,
          sortBy,
          sortDir,
        }
      });
      return res.data;
    },
    keepPreviousData: true,
    onSuccess: () => setPageInput(String(page)),
  });

  // Fetch status counts for chip badges
  const { data: statusCounts } = useQuery({
    queryKey: ['recipientStatusCounts'],
    queryFn: async () => {
      const res = await apiClient.get('/recipients/status-counts');
      return res.data as Record<string, number>;
    },
    staleTime: 30000
  });

  // Fetch lists for selectors
  const { data: lists = [] } = useQuery({
    queryKey: ['lists'],
    queryFn: async () => {
      const res = await apiClient.get('/lists');
      return res.data;
    }
  });

  // Fetch tag stats
  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const res = await apiClient.get('/recipients/tags');
      return res.data;
    }
  });

  // Validation report
  const { data: vrData, isLoading: vrLoading } = useQuery({
    queryKey: ['validationReport', vrPage, vrSearch, vrStatus],
    queryFn: async () => {
      const res = await apiClient.get('/recipients/validation-report', {
        params: { page: vrPage, limit: 50, search: vrSearch || undefined, status: vrStatus || undefined }
      });
      return res.data as {
        rows: Array<{
          _id: string; email: string; firstName?: string; lastName?: string;
          status: string; deliverabilityVerifiedAt?: string;
          validationData: {
            checkedAt: string; dnsValid: boolean; sesChecked: boolean;
            overallValidity?: string; hasValidSyntax?: string; hasValidDnsRecords?: string;
            mailboxExists?: string; isRoleAddress?: string; isDisposable?: string; isRandomInput?: string;
          };
        }>;
        pagination: { total: number; pages: number; page: number };
      };
    },
    enabled: activeTab === 'validation',
    keepPreviousData: true,
  });

  // Poll background deliverability job
  const { data: delivJob } = useQuery({
    queryKey: ['delivJob', currentJobId],
    queryFn: async () => {
      const res = await apiClient.get(`/recipients/deliverability-job/${currentJobId}`);
      return res.data as {
        status: 'running' | 'done' | 'error';
        phase: string;
        domainsTotal: number;
        domainsChecked: number;
        emailsToCheck: number;
        emailsChecked: number;
        emailsTotal: number;
        valid: number;
        invalid: Array<{ id: string; email: string; domain: string }>;
        unknown: Array<{ id: string; email: string; domain: string }>;
        suppressedBounce: Array<{ id: string; email: string; suppressedAt: string }>;
        suppressedComplaint: Array<{ id: string; email: string; suppressedAt: string }>;
        error?: string;
      };
    },
    enabled: !!currentJobId,
    refetchInterval: (data: any) => data?.status === 'running' ? 2000 : false,
  });

  // When job finishes, move results into delivResults
  useEffect(() => {
    if (!delivJob) return;
    if (delivJob.status === 'done') {
      setDelivResults({
        checked: delivJob.emailsTotal,
        valid: delivJob.valid,
        invalid: delivJob.invalid,
        unknown: delivJob.unknown,
        suppressedBounce: delivJob.suppressedBounce,
        suppressedComplaint: delivJob.suppressedComplaint,
      });
      setCurrentJobId(null);
      queryClient.invalidateQueries(['recipientStatusCounts']);
    } else if (delivJob.status === 'error') {
      showToast(delivJob.error || 'Deliverability check failed', 'error');
      setCurrentJobId(null);
    }
  }, [delivJob?.status]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors }
  } = useForm<RecipientFormValues>({
    resolver: zodResolver(recipientSchema)
  });

  // Inline email verification state (Add Recipient form only)
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [emailVerifyResult, setEmailVerifyResult] = useState<null | {
    dnsValid: boolean;
    dnsStatus: string;
    sesChecked?: boolean;
    suggestedStatus: string;
    sesDetail?: Record<string, string>;
    suppressedEntry?: { reason: string } | null;
  }>(null);

  const handleVerifyEmail = async () => {
    const email = watch('email');
    if (!email) return;
    setEmailVerifying(true);
    setEmailVerifyResult(null);
    try {
      const res = await apiClient.post('/recipients/verify-email', { email });
      setEmailVerifyResult(res.data);
      setValue('status', res.data.suggestedStatus, { shouldValidate: true });
    } catch {
      showToast('Verification failed', 'error');
    } finally {
      setEmailVerifying(false);
    }
  };

  // Save (Create/Update) mutation
  const saveMutation = useMutation({
    mutationFn: async (data: RecipientFormValues) => {
      const parsedTags = data.tags 
        ? data.tags.split(',').map(t => t.trim()).filter(t => t) 
        : [];
      
      const payload = {
        ...data,
        tags: parsedTags
      };

      if (isEditMode && editingId) {
        const res = await apiClient.put(`/recipients/${editingId}`, payload);
        return res.data;
      } else {
        const res = await apiClient.post('/recipients', payload);
        return res.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['recipients']);
      queryClient.invalidateQueries(['tags']);
      queryClient.invalidateQueries(['lists']); // update list counts
      showToast('Recipient saved successfully', 'success');
      setIsAddModalOpen(false);
      reset();
      setEmailVerifyResult(null);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to save recipient';
      showToast(msg, 'error');
    }
  });

  // Delete single mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/recipients/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['recipients']);
      queryClient.invalidateQueries(['recipientStatusCounts']);
      queryClient.invalidateQueries(['tags']);
      queryClient.invalidateQueries(['lists']);
      showToast('Recipient deleted successfully', 'success');
    },
    onError: () => {
      showToast('Failed to delete recipient', 'error');
    }
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/recipients/${id}/restore`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['recipients']);
      queryClient.invalidateQueries(['recipientStatusCounts']);
      showToast('Recipient restored successfully', 'success');
    },
    onError: () => {
      showToast('Failed to restore recipient', 'error');
    }
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await apiClient.post('/recipients/bulk-delete', { ids });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['recipients']);
      queryClient.invalidateQueries(['tags']);
      queryClient.invalidateQueries(['lists']);
      showToast('Selected recipients deleted successfully', 'success');
      setSelectedIds([]);
    },
    onError: () => {
      showToast('Failed to bulk delete recipients', 'error');
    }
  });

  // Poll import job status
  const pollImportJob = React.useCallback((jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const res = await apiClient.get(`/recipients/import-job/${jobId}`);
        const job = res.data;
        setImportJobStatus(job);
        if (job.status === 'done' || job.status === 'error') {
          clearInterval(interval);
          setImporting(false);
          if (job.status === 'done') {
            queryClient.invalidateQueries(['recipients']);
            queryClient.invalidateQueries(['tags']);
            queryClient.invalidateQueries(['lists']);
            showToast(
              `Import complete: ${job.imported} imported, ${job.failed} failed.`,
              job.failed > 0 ? 'info' : 'success'
            );
            setIsImportModalOpen(false);
            setCsvFile(null);
            setImportListId('');
            setImportJobId(null);
            setImportJobStatus(null);
            if (job.delivJobId) {
              setDelivResults(null);
              setCurrentJobId(job.delivJobId);
              setIsDelivModalOpen(true);
            }
          } else {
            showToast(job.error || 'Import failed', 'error');
          }
        }
      } catch {
        clearInterval(interval);
        setImporting(false);
      }
    }, 2000);
    return interval;
  }, [queryClient]);

  // Handle CSV Import
  const handleImportCSV = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile) return;

    setImporting(true);
    setImportJobStatus(null);
    const formData = new FormData();
    formData.append('file', csvFile);
    if (importListId) {
      formData.append('listId', importListId);
    }

    try {
      const res = await apiClient.post('/recipients/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { jobId } = res.data;
      setImportJobId(jobId);
      setImportJobStatus({ status: 'running', phase: 'Starting…', processed: 0, total: 0 });
      pollImportJob(jobId);
    } catch (err: any) {
      const msg = err.response?.data?.error || 'CSV import failed';
      showToast(msg, 'error');
      setImporting(false);
    }
  };

  const handleEditClick = (rec: any) => {
    setIsEditMode(true);
    setEditingId(rec._id || rec.id);
    setValue('email', rec.email);
    setValue('firstName', rec.firstName || '');
    setValue('lastName', rec.lastName || '');
    setValue('phone', rec.phone || '');
    setValue('city', rec.city || '');
    setValue('status', rec.status);
    setValue('tags', rec.tags?.join(', ') || '');
    setValue('listIds', rec.listIds?.map((l: any) => l._id || l.id) || []);
    setIsAddModalOpen(true);
  };

  const handleSelectCheckbox = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(item => item !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pageIds = recipients.map((r: any) => r._id || r.id);
      setSelectedIds(pageIds);
    } else {
      setSelectedIds([]);
    }
  };

  const onSubmit = (data: RecipientFormValues) => {
    saveMutation.mutate(data);
  };

  const handleRunDelivCheck = async () => {
    setDelivResults(null);
    try {
      const res = await apiClient.post('/recipients/check-deliverability', {
        scope: delivScope,
        ids: delivScope === 'selected' ? selectedIds : undefined,
        limit: delivScope === 'limit' ? parseInt(delivLimit, 10) || 10000 : undefined,
      });
      setCurrentJobId(res.data.jobId);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to start deliverability check', 'error');
    }
  };

  const handleQuickStatus = async (id: string, status: string) => {
    setQuickStatusLoading(id);
    setStatusMenuId(null);
    try {
      await apiClient.put(`/recipients/${id}`, { status });
      queryClient.invalidateQueries(['recipients']);
      queryClient.invalidateQueries(['recipientStatusCounts']);
      showToast(`Status updated to ${status}`, 'success');
    } catch {
      showToast('Failed to update status', 'error');
    } finally {
      setQuickStatusLoading(null);
    }
  };

  const handleApplyDelivResults = async () => {
    if (!delivResults) return;
    setApplyingResults(true);
    try {
      await apiClient.post('/recipients/apply-deliverability', {
        invalidIds:            delivResults.invalid.map(r => r.id),
        suppressedBounceIds:   delivResults.suppressedBounce.map(r => r.id),
        suppressedComplaintIds: delivResults.suppressedComplaint.map(r => r.id),
        unknownIds:            delivResults.unknown.map(r => r.id),
      });
      queryClient.invalidateQueries(['recipients']);
      queryClient.invalidateQueries(['recipientStatusCounts']);
      const total = delivResults.invalid.length + delivResults.suppressedBounce.length + delivResults.suppressedComplaint.length + delivResults.unknown.length;
      showToast(`${total} recipient${total !== 1 ? 's' : ''} moved to appropriate status`, 'success');
      setIsDelivModalOpen(false);
      setDelivResults(null);
    } catch {
      showToast('Failed to apply results', 'error');
    } finally {
      setApplyingResults(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
    setPageInput('1');
  };

  const goToPage = (raw: string) => {
    const n = parseInt(raw, 10);
    const total = recipientsData?.pagination?.pages || 1;
    if (!isNaN(n) && n >= 1 && n <= total) {
      setPage(n);
      setPageInput(String(n));
    } else {
      setPageInput(String(page)); // revert bad input
    }
  };

  // Close status menu on outside click
  useEffect(() => {
    if (!statusMenuId) return;
    const close = () => setStatusMenuId(null);
    document.addEventListener('click', close, { capture: true });
    return () => document.removeEventListener('click', close, { capture: true });
  }, [statusMenuId]);

  const recipients = recipientsData?.recipients || [];
  const pagination = recipientsData?.pagination || { total: 0, pages: 1 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase">Recipients</h2>
          <p className="text-xs text-slate-550 dark:text-slate-400">Import CSV lists or manage subscriber metadata.</p>
        </div>

        <div className="flex gap-2 self-start flex-wrap">
          <button
            onClick={() => {
              setDelivScope(selectedIds.length > 0 ? 'selected' : 'subscribed');
              setDelivResults(null);
              setIsDelivModalOpen(true);
            }}
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <ShieldCheck size={16} />
            Check Deliverability
          </button>
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <Upload size={16} />
            Bulk Import CSV
          </button>
          <button
            onClick={() => {
              setIsEditMode(false);
              setEditingId(null);
              reset();
              setIsAddModalOpen(true);
            }}
            className="btn-primary flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            Add Recipient
          </button>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800/60 rounded-xl w-fit">
        {([
          { id: 'recipients', label: 'Recipients' },
          { id: 'validation', label: 'Validation Report' },
        ] as const).map(tab => (
          <button
            key={tab.id}
            onClick={() => setTab(tab.id as 'recipients' | 'validation')}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              activeTab === tab.id
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Quick status filter chips */}
      {activeTab === 'recipients' && <div className="flex flex-wrap gap-2">
        {[
          { label: 'All',          value: '',             activeClass: 'bg-primary-500/15 text-primary-500 border-primary-500/40' },
          { label: 'Subscribed',   value: 'SUBSCRIBED',   activeClass: 'bg-emerald-500/15 text-accent-emerald border-emerald-500/40' },
          { label: 'Unsubscribed', value: 'UNSUBSCRIBED', activeClass: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600' },
          { label: 'Bounced',      value: 'BOUNCED',      activeClass: 'bg-rose-500/15 text-accent-rose border-rose-500/40' },
          { label: 'Complained',   value: 'COMPLAINED',   activeClass: 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/40' },
          { label: 'Invalid',      value: 'INVALID',      activeClass: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/40' },
          { label: 'Deleted',      value: 'DELETED',      activeClass: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/40' },
        ].map(chip => {
          const active = statusFilter === chip.value;
          const count = chip.value ? statusCounts?.[chip.value] : undefined;
          return (
            <button
              key={chip.value}
              onClick={() => { setStatusFilter(chip.value); setPage(1); /* reset page on filter change */ }}
              className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors flex items-center gap-1.5 ${
                active
                  ? chip.activeClass
                  : 'border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:border-primary-300 dark:hover:border-white/20'
              }`}
            >
              {chip.label}
              {count !== undefined && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  active ? 'bg-white/30' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                }`}>
                  {count.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
      </div>}

      {activeTab === 'recipients' && <>
      {/* Filter and Search Panel */}
      <div className="glass-panel rounded-2xl p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:max-w-xs">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
            <Search size={16} />
          </span>
          <input
            type="text"
            placeholder="Search email, name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input w-full pl-10"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* List Filter */}
          <div className="w-48">
            <SearchableSelect
              label=""
              placeholder="All Lists"
              options={[{ value: '', label: 'All Lists' }, ...lists.map((l: any) => ({ value: l.id, label: l.name }))]}
              value={listFilter}
              onChange={(v) => { setListFilter(v); setPage(1); }}
            />
          </div>

          {/* Tag Filter */}
          <select
            value={tagFilter}
            onChange={(e) => {
              setTagFilter(e.target.value);
              setPage(1);
            }}
            className="form-input bg-white dark:bg-slate-900"
          >
            <option value="" className="bg-white dark:bg-slate-900">All Tags</option>
            {tags.map((t: any) => (
              <option key={t._id} value={t._id} className="bg-white dark:bg-slate-900">{t._id} ({t.count})</option>
            ))}
          </select>
          
          {/* Bulk actions (Delete) */}
          {selectedIds.length > 0 && user?.role === 'ADMIN' && (
            <button
              onClick={() => openConfirm(
                'Delete Recipients',
                `Are you sure you want to delete ${selectedIds.length.toLocaleString()} recipient${selectedIds.length > 1 ? 's' : ''}? This cannot be undone.`,
                () => bulkDeleteMutation.mutate(selectedIds)
              )}
              className="btn-danger py-2 px-3 text-xs flex items-center gap-1.5"
            >
              <Trash2 size={14} />
              Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>
      </div>

      {/* Recipient Table */}
      <div className={`glass-panel rounded-2xl overflow-hidden transition-opacity duration-150 ${isPreviousData ? 'opacity-60' : 'opacity-100'}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">
                <th className="py-4 px-6 w-12">
                  <input
                    type="checkbox"
                    checked={recipients.length > 0 && selectedIds.length === recipients.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 focus:ring-primary-500"
                  />
                </th>
                {[
                  { label: 'Email & Subscriber', field: 'email' },
                  { label: 'Location / Phone',   field: null },
                  { label: 'Associated Lists & Tags', field: null },
                  { label: 'Status',             field: 'status' },
                ].map(col => (
                  <th
                    key={col.label}
                    className={`py-4 px-6 ${col.field ? 'cursor-pointer select-none hover:text-slate-800 dark:hover:text-white' : ''}`}
                    onClick={() => col.field && handleSort(col.field)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.field && sortBy === col.field && (
                        <span className="text-primary-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
                      )}
                      {col.field && sortBy !== col.field && (
                        <span className="opacity-30">↕</span>
                      )}
                    </span>
                  </th>
                ))}
                <th className="py-4 px-6 text-right">
                  <span className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-slate-800 dark:hover:text-white" onClick={() => handleSort('createdAt')}>
                    Date Added
                    {sortBy === 'createdAt' && <span className="text-primary-500">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                    {sortBy !== 'createdAt' && <span className="opacity-30">↕</span>}
                  </span>
                </th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-white/5 text-sm text-slate-600 dark:text-slate-300">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </td>
                </tr>
              ) : recipients.map((rec: any) => {
                const badgeColor: Record<string, string> = {
                  SUBSCRIBED:   'bg-emerald-500/10 text-emerald-650 dark:text-accent-emerald border-emerald-500/20',
                  UNSUBSCRIBED: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700',
                  BOUNCED:      'bg-rose-500/10 text-rose-555 dark:text-accent-rose border-rose-500/20',
                  COMPLAINED:   'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
                  INVALID:      'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
                };

                const recId = rec._id || rec.id;

                return (
                  <tr key={recId} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(recId)}
                        onChange={(e) => handleSelectCheckbox(recId, e.target.checked)}
                        className="rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-semibold text-slate-800 dark:text-white">{rec.email}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {rec.firstName || rec.lastName ? `${rec.firstName || ''} ${rec.lastName || ''}` : '--'}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-xs text-slate-700 dark:text-slate-200">{rec.city || '--'}</div>
                      <div className="text-xs text-slate-500">{rec.phone || '--'}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex flex-col gap-1.5">
                        {/* Lists display */}
                        {rec.listIds && rec.listIds.length > 0 && (
                          <div className="text-xs">
                            Lists: <strong className="text-slate-700 dark:text-slate-200">{rec.listIds.map((l: any) => l.name).join(', ')}</strong>
                          </div>
                        )}
                        {/* Tags display */}
                        {rec.tags && rec.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {rec.tags.map((t: string) => (
                              <span key={t} className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-1.5 py-0.5 rounded border border-slate-200 dark:border-white/5">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="relative">
                        {quickStatusLoading === recId ? (
                          <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <button
                            onClick={() => setStatusMenuId(statusMenuId === recId ? null : recId)}
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold border cursor-pointer hover:opacity-80 transition-opacity ${badgeColor[rec.status] || 'bg-slate-100 dark:bg-slate-800 text-slate-600 border-slate-200'}`}
                            title="Click to change status"
                          >
                            {rec.status}
                          </button>
                        )}
                        {statusMenuId === recId && (
                          <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-xl shadow-xl overflow-hidden min-w-[150px]">
                            {(['SUBSCRIBED', 'UNSUBSCRIBED', 'BOUNCED', 'COMPLAINED', 'INVALID'] as const).filter(s => s !== rec.status).map(s => (
                              <button
                                key={s}
                                onClick={() => handleQuickStatus(recId, s)}
                                className="w-full text-left px-3 py-2 text-xs font-medium hover:bg-slate-50 dark:hover:bg-white/5 transition-colors flex items-center gap-2"
                              >
                                <span className={`w-2 h-2 rounded-full ${
                                  s === 'SUBSCRIBED' ? 'bg-emerald-500' :
                                  s === 'BOUNCED' ? 'bg-rose-500' :
                                  s === 'COMPLAINED' ? 'bg-orange-500' :
                                  s === 'INVALID' ? 'bg-amber-500' : 'bg-slate-400'
                                }`} />
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right text-xs text-slate-400 whitespace-nowrap">
                      {rec.createdAt ? new Date(rec.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditClick(rec)}
                          className="text-slate-500 dark:text-slate-400 hover:text-slate-850 dark:hover:text-white p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                          title="Edit Subscriber"
                        >
                          <Edit3 size={15} />
                        </button>
                        {user?.role === 'ADMIN' && statusFilter !== 'DELETED' && (
                          <button
                            onClick={() => openConfirm(
                              'Delete Recipient',
                              `Delete ${rec.email}? This cannot be undone.`,
                              () => deleteMutation.mutate(recId)
                            )}
                            className="text-slate-500 dark:text-slate-400 hover:text-accent-rose p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                            title="Delete Subscriber"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                        {statusFilter === 'DELETED' && (
                          <button
                            onClick={() => restoreMutation.mutate(recId)}
                            className="text-slate-500 dark:text-slate-400 hover:text-emerald-500 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                            title="Restore Recipient"
                          >
                            <RotateCcw size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!isLoading && recipients.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">
                    No recipients matching filter criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(pagination.pages > 1 || page > 1) && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-white/5 flex items-center justify-between flex-wrap gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {pagination.total > 0
                ? `${((page - 1) * 50 + 1).toLocaleString()}–${Math.min(page * 50, pagination.total).toLocaleString()} of ${pagination.total.toLocaleString()}`
                : 'No results'}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => { setPage(1); setPageInput('1'); }}
                className="btn-secondary px-2 py-1.5 flex items-center disabled:opacity-50 text-xs"
                title="First page"
              >
                «
              </button>
              <button
                disabled={page === 1}
                onClick={() => { const p = page - 1; setPage(p); setPageInput(String(p)); }}
                className="btn-secondary px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
              >
                <ChevronLeft size={14} /> Prev
              </button>

              {/* Direct page number input */}
              <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <span>Page</span>
                <input
                  type="number"
                  min={1}
                  max={pagination.pages || 1}
                  value={pageInput}
                  onChange={e => setPageInput(e.target.value)}
                  onBlur={e => goToPage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && goToPage(pageInput)}
                  className="w-14 text-center form-input py-1 px-2 text-xs"
                />
                <span>of {(pagination.pages || 1).toLocaleString()}</span>
              </div>

              <button
                disabled={page >= (pagination.pages || 1)}
                onClick={() => { const p = page + 1; setPage(p); setPageInput(String(p)); }}
                className="btn-secondary px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
              >
                Next <ChevronRight size={14} />
              </button>
              <button
                disabled={page >= (pagination.pages || 1)}
                onClick={() => { const p = pagination.pages; setPage(p); setPageInput(String(p)); }}
                className="btn-secondary px-2 py-1.5 flex items-center disabled:opacity-50 text-xs"
                title="Last page"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
      </>}

      {/* ── Validation Report Tab ─────────────────────────────────────── */}
      {activeTab === 'validation' && (() => {
        // Standard columns: HIGH = good (green), LOW = bad (red)
        const verdict = (v?: string) => {
          if (!v) return <span className="text-slate-400 text-[10px]">—</span>;
          const color = v === 'HIGH' ? 'text-emerald-500' : v === 'MEDIUM' ? 'text-amber-500' : 'text-rose-500';
          return <span className={`text-[10px] font-bold ${color}`}>{v}</span>;
        };
        // Inverted columns: LOW = good (green), HIGH = bad (red)
        const verdictInv = (v?: string) => {
          if (!v) return <span className="text-slate-400 text-[10px]">—</span>;
          const color = v === 'LOW' ? 'text-emerald-500' : v === 'MEDIUM' ? 'text-amber-500' : 'text-rose-500';
          return <span className={`text-[10px] font-bold ${color}`}>{v}</span>;
        };
        const vrRows = vrData?.rows || [];
        const vrPag  = vrData?.pagination || { total: 0, pages: 1, page: 1 };
        const goVrPage = (raw: string) => {
          const n = parseInt(raw, 10);
          const max = vrPag.pages || 1;
          if (!isNaN(n) && n >= 1 && n <= max) { setVrPage(n); setVrPageInput(String(n)); }
          else setVrPageInput(String(vrPage));
        };

        return (
          <div className="space-y-4">
            {/* Search + filter bar */}
            <div className="glass-panel rounded-2xl p-4 flex flex-col md:flex-row gap-4 items-center">
              <div className="relative w-full md:max-w-xs">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500"><Search size={16} /></span>
                <input
                  type="text"
                  placeholder="Search email…"
                  value={vrSearch}
                  onChange={e => { setVrSearch(e.target.value); setVrPage(1); }}
                  className="form-input w-full pl-10"
                />
              </div>
              <select value={vrStatus} onChange={e => { setVrStatus(e.target.value); setVrPage(1); }} className="form-input bg-white dark:bg-slate-900">
                <option value="">All Statuses</option>
                {['SUBSCRIBED','UNSUBSCRIBED','BOUNCED','COMPLAINED','INVALID'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <span className="text-xs text-slate-400 ml-auto">{vrPag.total.toLocaleString()} checked emails</span>
            </div>

            {/* Final Status legend */}
            <div className="glass-panel rounded-2xl p-4 text-[11px] text-slate-500 dark:text-slate-400 space-y-1.5">
              <p className="font-semibold text-slate-700 dark:text-slate-200 text-xs">How Final Status is determined</p>
              <p>An email is marked <span className="text-rose-500 font-bold">INVALID</span> if any of the following are true: DNS check failed, Overall validity is LOW, Syntax is LOW, DNS Records is LOW, or Mailbox exists is LOW.</p>
              <p>It is marked <span className="text-orange-500 font-bold">BOUNCED / COMPLAINED</span> if AWS SES has it on the suppression list. Otherwise it stays <span className="text-emerald-500 font-bold">SUBSCRIBED</span>.</p>
              <p className="text-slate-400 dark:text-slate-500">
                Color key — standard columns (Overall, Syntax, DNS Records, Mailbox): <span className="text-emerald-500 font-bold">HIGH</span> = good · <span className="text-amber-500 font-bold">MEDIUM</span> = warning · <span className="text-rose-500 font-bold">LOW</span> = bad.
                {' '}Inverted columns (Role Addr, Disposable, Random): <span className="text-emerald-500 font-bold">LOW</span> = good · <span className="text-rose-500 font-bold">HIGH</span> = bad.
              </p>
            </div>

            {/* Table */}
            <div className="glass-panel rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400 text-[10px] font-semibold uppercase tracking-wider">
                      <th className="py-3 px-4 min-w-[200px]">Email</th>
                      <th className="py-3 px-3 text-center">DNS Valid</th>
                      <th className="py-3 px-3 text-center">Overall</th>
                      <th className="py-3 px-3 text-center">Syntax</th>
                      <th className="py-3 px-3 text-center">DNS Records</th>
                      <th className="py-3 px-3 text-center">Mailbox</th>
                      <th className="py-3 px-3 text-center">Role Addr</th>
                      <th className="py-3 px-3 text-center">Disposable</th>
                      <th className="py-3 px-3 text-center">Random</th>
                      <th className="py-3 px-3 text-center">Final Status</th>
                      <th className="py-3 px-4 text-right">Checked At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                    {vrLoading && (
                      <tr><td colSpan={11} className="text-center py-10 text-slate-400">Loading…</td></tr>
                    )}
                    {!vrLoading && vrRows.length === 0 && (
                      <tr><td colSpan={11} className="text-center py-10 text-slate-400">
                        No checked emails yet. Run a deliverability check to populate this report.
                      </td></tr>
                    )}
                    {vrRows.map(row => {
                      const vd = row.validationData;
                      const statusColor =
                        row.status === 'SUBSCRIBED'   ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' :
                        row.status === 'BOUNCED'      ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400' :
                        row.status === 'COMPLAINED'   ? 'bg-orange-500/15 text-orange-600 dark:text-orange-400' :
                        row.status === 'INVALID'      ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' :
                        'bg-slate-100 dark:bg-slate-800 text-slate-500';
                      return (
                        <tr key={row._id} className="hover:bg-slate-50 dark:hover:bg-white/2">
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-800 dark:text-white truncate max-w-[220px]">{row.email}</div>
                            {(row.firstName || row.lastName) && (
                              <div className="text-slate-400 text-[10px]">{[row.firstName, row.lastName].filter(Boolean).join(' ')}</div>
                            )}
                          </td>
                          <td className="py-3 px-3 text-center">
                            {vd.dnsValid
                              ? <CheckCircle2 size={13} className="text-emerald-500 mx-auto" />
                              : <XCircle      size={13} className="text-rose-500 mx-auto" />}
                          </td>
                          <td className="py-3 px-3 text-center">{verdict(vd.overallValidity)}</td>
                          <td className="py-3 px-3 text-center">{verdict(vd.hasValidSyntax)}</td>
                          <td className="py-3 px-3 text-center">{verdict(vd.hasValidDnsRecords)}</td>
                          <td className="py-3 px-3 text-center">{verdict(vd.mailboxExists)}</td>
                          <td className="py-3 px-3 text-center">{verdictInv(vd.isRoleAddress)}</td>
                          <td className="py-3 px-3 text-center">{verdictInv(vd.isDisposable)}</td>
                          <td className="py-3 px-3 text-center">{verdictInv(vd.isRandomInput)}</td>
                          <td className="py-3 px-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColor}`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-slate-400 text-[10px] whitespace-nowrap">
                            {new Date(vd.checkedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {(vrPag.pages > 1 || vrPage > 1) && (
                <div className="px-6 py-4 border-t border-slate-200 dark:border-white/5 flex items-center justify-between flex-wrap gap-3">
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {vrPag.total > 0
                      ? `${((vrPage - 1) * 50 + 1).toLocaleString()}–${Math.min(vrPage * 50, vrPag.total).toLocaleString()} of ${vrPag.total.toLocaleString()}`
                      : 'No results'}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={vrPage === 1}
                      onClick={() => { setVrPage(1); setVrPageInput('1'); }}
                      className="btn-secondary px-2 py-1.5 text-xs disabled:opacity-50"
                      title="First page"
                    >«</button>
                    <button
                      disabled={vrPage === 1}
                      onClick={() => { const p = vrPage - 1; setVrPage(p); setVrPageInput(String(p)); }}
                      className="btn-secondary px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
                    ><ChevronLeft size={14} /> Prev</button>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                      <span>Page</span>
                      <input
                        type="number"
                        min={1}
                        max={vrPag.pages || 1}
                        value={vrPageInput}
                        onChange={e => setVrPageInput(e.target.value)}
                        onBlur={e => goVrPage(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && goVrPage(vrPageInput)}
                        className="w-14 text-center form-input py-1 px-2 text-xs"
                      />
                      <span>of {(vrPag.pages || 1).toLocaleString()}</span>
                    </div>
                    <button
                      disabled={vrPage >= (vrPag.pages || 1)}
                      onClick={() => { const p = vrPage + 1; setVrPage(p); setVrPageInput(String(p)); }}
                      className="btn-secondary px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
                    >Next <ChevronRight size={14} /></button>
                    <button
                      disabled={vrPage >= (vrPag.pages || 1)}
                      onClick={() => { const p = vrPag.pages; setVrPage(p); setVrPageInput(String(p)); }}
                      className="btn-secondary px-2 py-1.5 text-xs disabled:opacity-50"
                      title="Last page"
                    >»</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Add/Edit Recipient Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => { setIsAddModalOpen(false); setEmailVerifyResult(null); }}
        title={isEditMode ? 'Edit Recipient' : 'Add Recipient'}
        size="lg"
        align="top"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

          {/* ── Email + Verify ─────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Address</label>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="customer@email.com"
                disabled={isEditMode}
                {...register('email')}
                onChange={e => { register('email').onChange(e); setEmailVerifyResult(null); }}
                className="form-input flex-1 disabled:opacity-50"
              />
              {!isEditMode && (
                <button
                  type="button"
                  onClick={handleVerifyEmail}
                  disabled={emailVerifying || !watch('email')}
                  className="btn-secondary px-4 flex items-center gap-1.5 shrink-0 disabled:opacity-40"
                >
                  {emailVerifying
                    ? <><span className="w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /> Checking…</>
                    : <><ShieldCheck size={14} /> Verify</>}
                </button>
              )}
            </div>
            {errors.email && <span className="text-xs text-rose-500">{errors.email.message}</span>}

            {/* Verification result badge */}
            {emailVerifyResult && (() => {
              const r = emailVerifyResult;
              const ok = r.suggestedStatus === 'SUBSCRIBED';
              const warn = r.suggestedStatus === 'BOUNCED' || r.suggestedStatus === 'COMPLAINED';
              const bg = ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                : warn ? 'bg-orange-500/10 border-orange-500/30 text-orange-700 dark:text-orange-400'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400';
              const icon = ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />;
              const label = ok ? 'Email looks deliverable'
                : r.suggestedStatus === 'BOUNCED' ? 'Found on SES bounce suppression list'
                : r.suggestedStatus === 'COMPLAINED' ? 'Found on SES complaint suppression list'
                : !r.dnsValid ? `Domain has no mail server (${r.dnsStatus})`
                : 'Failed deliverability check';
              return (
                <div className={`flex items-start gap-2 p-3 rounded-xl border text-xs mt-1 ${bg}`}>
                  <span className="mt-0.5 shrink-0">{icon}</span>
                  <div className="space-y-1">
                    <p className="font-semibold">{label} — status set to <strong>{r.suggestedStatus}</strong></p>
                    {r.sesDetail && (
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] opacity-80">
                        {Object.entries(r.sesDetail).filter(([,v]) => v).map(([k, v]) => (
                          <span key={k}>{k.replace(/([A-Z])/g, ' $1').trim()}: <strong>{v as string}</strong></span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── Status pills ──────────────────────────────────── */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Status
              {emailVerifyResult && <span className="ml-2 text-primary-500 font-normal normal-case">(auto-set from verification — you can override)</span>}
            </label>
            <div className="flex flex-wrap gap-2">
              {(['SUBSCRIBED','UNSUBSCRIBED','BOUNCED','COMPLAINED','INVALID'] as const).map(s => {
                const current = watch('status');
                const colors: Record<string, string> = {
                  SUBSCRIBED:   'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
                  UNSUBSCRIBED: 'border-slate-400 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
                  BOUNCED:      'border-rose-500 bg-rose-500/10 text-rose-700 dark:text-rose-400',
                  COMPLAINED:   'border-orange-500 bg-orange-500/10 text-orange-700 dark:text-orange-400',
                  INVALID:      'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400',
                };
                const inactive = 'border-slate-200 dark:border-white/10 text-slate-500 hover:border-slate-300 dark:hover:border-white/20';
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setValue('status', s, { shouldValidate: true })}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${current === s ? colors[s] : inactive}`}
                  >
                    {s.charAt(0) + s.slice(1).toLowerCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Name row ─────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">First Name</label>
              <input type="text" placeholder="John" {...register('firstName')} className="form-input" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Last Name</label>
              <input type="text" placeholder="Doe" {...register('lastName')} className="form-input" />
            </div>
          </div>

          {/* ── Phone + City ─────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Phone</label>
              <input type="text" placeholder="+91 98765 43210" {...register('phone')} className="form-input" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">City</label>
              <input type="text" placeholder="Mumbai" {...register('city')} className="form-input" />
            </div>
          </div>

          {/* ── Tags ─────────────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tags <span className="normal-case font-normal text-slate-400">(comma-separated)</span></label>
            <input type="text" placeholder="leads, vip, summer2026" {...register('tags')} className="form-input" />
          </div>

          {/* ── Lists ────────────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Associated Lists</label>
            <div className="max-h-40 overflow-y-auto grid grid-cols-2 gap-x-4 gap-y-2 p-3 bg-slate-50 dark:bg-slate-950/40 rounded-xl border border-slate-200 dark:border-white/5">
              {lists.map((l: any) => (
                <label key={l.id} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white cursor-pointer select-none">
                  <input
                    type="checkbox"
                    value={l.id}
                    {...register('listIds')}
                    className="rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-primary-500 focus:ring-primary-500"
                  />
                  {l.name}
                </label>
              ))}
              {lists.length === 0 && <span className="text-xs text-slate-500 col-span-2">No lists created yet.</span>}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button type="button" onClick={() => { setIsAddModalOpen(false); setEmailVerifyResult(null); }} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saveMutation.isLoading} className="btn-primary flex items-center gap-2">
              {saveMutation.isLoading ? 'Saving…' : isEditMode ? 'Save Changes' : 'Add Recipient'}
            </button>
          </div>
        </form>
      </Modal>

      {/* CSV Import Modal */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => {
          if (importing) return; // don't close while running
          setIsImportModalOpen(false);
          setCsvFile(null);
          setImportListId('');
        }}
        title="Bulk Import Subscribers (CSV)"
        size="lg"
      >
        {importing && importJobStatus ? (
          /* Progress view */
          <div className="space-y-5 py-2">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin shrink-0"></div>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{importJobStatus.phase || 'Processing…'}</span>
            </div>
            {importJobStatus.total > 0 && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-slate-400">
                  <span>{importJobStatus.processed?.toLocaleString()} / {importJobStatus.total?.toLocaleString()}</span>
                  <span>{Math.round((importJobStatus.processed / importJobStatus.total) * 100)}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.round((importJobStatus.processed / importJobStatus.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            <p className="text-xs text-slate-400">This may take a few minutes for large files. You can keep working — this dialog will close automatically when done.</p>
          </div>
        ) : (
        <form onSubmit={handleImportCSV} className="space-y-4">
          <div className="p-4 bg-primary-500/10 border border-primary-500/20 rounded-xl flex items-start gap-3">
            <FileSpreadsheet className="text-primary-500 shrink-0 mt-0.5" size={20} />
            <div className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              <span className="font-semibold text-slate-800 dark:text-white block mb-0.5">CSV File Requirements:</span>
              Your CSV must contain an <strong>email</strong> column. Other supported headers:
              <strong>firstName</strong>, <strong>lastName</strong>, <strong>phone</strong>, <strong>city</strong>, <strong>tags</strong> (separated by comma).
            </div>
          </div>

          {/* File Picker */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Choose CSV File</label>
            <input
              type="file"
              accept=".csv"
              required
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              className="form-input w-full text-xs"
            />
          </div>

          {/* Associated List for import */}
          <SearchableSelect
            label="Associate with List (Optional)"
            placeholder="-- Do not associate with a list --"
            options={[{ value: '', label: '-- Do not associate with a list --' }, ...lists.map((l: any) => ({ value: l.id, label: l.name }))]}
            value={importListId}
            onChange={setImportListId}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => setIsImportModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={importing || !csvFile}
              className="btn-primary flex items-center gap-1.5"
            >
              <Upload size={14} />
              Start Upload
            </button>
          </div>
        </form>
        )}
      </Modal>
      {/* Deliverability Check Modal */}
      <Modal
        isOpen={isDelivModalOpen}
        onClose={() => {
          if (!currentJobId) {
            setIsDelivModalOpen(false);
            setDelivResults(null);
          }
        }}
        title="Check Email Deliverability"
        size="lg"
        align="top"
      >
        {!delivResults && !currentJobId && (
          <div className="space-y-5">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Verifies DNS MX records for each email domain. Emails whose domain has no mail servers will show as invalid — these can be safely marked as Bounced to keep your list clean.
            </p>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">Scope</label>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  delivScope === 'subscribed'
                    ? 'border-primary-500/50 bg-primary-500/5'
                    : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20'
                }`}>
                  <input
                    type="radio"
                    name="delivScope"
                    value="subscribed"
                    checked={delivScope === 'subscribed'}
                    onChange={() => setDelivScope('subscribed')}
                    className="mt-0.5 accent-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-800 dark:text-white">
                      Unverified Subscribed
                      {statusCounts?.UNVERIFIED_SUBSCRIBED !== undefined && (
                        <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">({statusCounts.UNVERIFIED_SUBSCRIBED.toLocaleString()} not yet checked)</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Skips already-verified emails — only checks new/unchecked ones</div>
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                  selectedIds.length === 0
                    ? 'border-slate-200 dark:border-white/10 opacity-40 cursor-not-allowed'
                    : delivScope === 'selected'
                    ? 'border-primary-500/50 bg-primary-500/5 cursor-pointer'
                    : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 cursor-pointer'
                }`}>
                  <input
                    type="radio"
                    name="delivScope"
                    value="selected"
                    disabled={selectedIds.length === 0}
                    checked={delivScope === 'selected'}
                    onChange={() => setDelivScope('selected')}
                    className="mt-0.5 accent-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-800 dark:text-white">
                      Selected Only
                      {selectedIds.length > 0 && (
                        <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">({selectedIds.length.toLocaleString()} selected)</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {selectedIds.length === 0 ? 'Select recipients from the table first' : "Check only the rows you've checked"}
                    </div>
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  delivScope === 'limit'
                    ? 'border-primary-500/50 bg-primary-500/5'
                    : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20'
                }`}>
                  <input
                    type="radio"
                    name="delivScope"
                    value="limit"
                    checked={delivScope === 'limit'}
                    onChange={() => setDelivScope('limit')}
                    className="mt-0.5 accent-primary-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800 dark:text-white">Check N unverified emails</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 mb-2">Pick the oldest unverified subscribed emails up to a count you choose</div>
                    {delivScope === 'limit' && (
                      <input
                        type="number"
                        min={1}
                        max={500000}
                        value={delivLimit}
                        onChange={e => setDelivLimit(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        placeholder="e.g. 10000"
                        className="form-input w-40 py-1.5 px-3 text-sm"
                      />
                    )}
                  </div>
                </label>
              </div>
            </div>

            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-2">
              <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Runs as a background job — you can navigate away. Progress updates every 2 seconds. Large lists (250k+) may take 10–20 minutes.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-slate-200 dark:border-white/5">
              <button onClick={() => setIsDelivModalOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleRunDelivCheck} className="btn-primary flex items-center gap-2">
                <ShieldCheck size={15} />
                Run Check
              </button>
            </div>
          </div>
        )}

        {currentJobId && (
          <div className="py-10 space-y-6">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-800 dark:text-white">
                  {delivJob?.phase || 'Starting…'}
                </p>
                {delivJob && delivJob.emailsTotal > 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {delivJob.emailsTotal.toLocaleString()} emails
                  </p>
                )}
              </div>
            </div>

            {delivJob && delivJob.domainsTotal > 0 && (
              <div className="space-y-4 w-full">
                {/* Step 1: DNS domain check */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                    <span>Step 1 — DNS domain check</span>
                    <span>{delivJob.domainsChecked.toLocaleString()} / {delivJob.domainsTotal.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 bg-primary-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.round((delivJob.domainsChecked / delivJob.domainsTotal) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Step 2: per-email SES check — only appears once DNS is done */}
                {delivJob.emailsToCheck > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>Step 2 — Individual email check (SES)</span>
                      <span>{delivJob.emailsChecked.toLocaleString()} / {delivJob.emailsToCheck.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 bg-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((delivJob.emailsChecked / delivJob.emailsToCheck) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Waiting for step 2 to start */}
                {delivJob.domainsChecked >= delivJob.domainsTotal && delivJob.emailsToCheck === 0 && (
                  <p className="text-xs text-slate-400 text-center">Preparing email validation…</p>
                )}
              </div>
            )}

            <p className="text-center text-xs text-slate-400">Job is running in the background — you can leave this page and come back.</p>
          </div>
        )}

        {delivResults && !currentJobId && (() => {
          const totalProblematic = delivResults.invalid.length + delivResults.suppressedBounce.length + delivResults.suppressedComplaint.length + delivResults.unknown.length;
          return (
          <div className="space-y-5">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Checked <strong className="text-slate-800 dark:text-white">{delivResults.checked.toLocaleString()}</strong> recipients
              {delivResults.valid > 0 && <span className="text-emerald-600 dark:text-emerald-400"> — {delivResults.valid.toLocaleString()} verified and stamped ✓</span>}
            </p>

            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <CheckCircle2 size={18} className="text-emerald-500" />
                <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{delivResults.valid.toLocaleString()}</span>
                <span className="text-[10px] text-slate-500 text-center">Valid</span>
              </div>
              <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle size={18} className="text-amber-500" />
                <span className="text-lg font-bold text-amber-600 dark:text-amber-400">{delivResults.invalid.length.toLocaleString()}</span>
                <span className="text-[10px] text-slate-500 text-center">→ INVALID</span>
              </div>
              <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20">
                <XCircle size={18} className="text-rose-500" />
                <span className="text-lg font-bold text-rose-600 dark:text-accent-rose">{delivResults.suppressedBounce.length.toLocaleString()}</span>
                <span className="text-[10px] text-slate-500 text-center">→ BOUNCED</span>
              </div>
              <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-orange-500/10 border border-orange-500/20">
                <AlertTriangle size={18} className="text-orange-500" />
                <span className="text-lg font-bold text-orange-600 dark:text-orange-400">{delivResults.suppressedComplaint.length.toLocaleString()}</span>
                <span className="text-[10px] text-slate-500 text-center">→ COMPLAINED</span>
              </div>
              <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-white/10">
                <HelpCircle size={18} className="text-slate-400" />
                <span className="text-lg font-bold text-slate-600 dark:text-slate-300">{delivResults.unknown.length.toLocaleString()}</span>
                <span className="text-[10px] text-slate-500 text-center">Unknown</span>
              </div>
            </div>

            {/* SES Bounce suppressed */}
            {delivResults.suppressedBounce.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-rose-500 uppercase tracking-wider">SES Suppressed — Bounce</p>
                <div className="max-h-32 overflow-y-auto rounded-xl border border-rose-500/20 divide-y divide-rose-500/10">
                  {delivResults.suppressedBounce.map(r => (
                    <div key={r.id} className="flex items-center justify-between px-4 py-2 text-sm bg-rose-500/5">
                      <span className="text-slate-800 dark:text-white font-medium truncate max-w-[65%]">{r.email}</span>
                      <span className="text-[10px] text-slate-400">{new Date(r.suppressedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SES Complaint suppressed */}
            {delivResults.suppressedComplaint.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wider">SES Suppressed — Complaint</p>
                <div className="max-h-32 overflow-y-auto rounded-xl border border-orange-500/20 divide-y divide-orange-500/10">
                  {delivResults.suppressedComplaint.map(r => (
                    <div key={r.id} className="flex items-center justify-between px-4 py-2 text-sm bg-orange-500/5">
                      <span className="text-slate-800 dark:text-white font-medium truncate max-w-[65%]">{r.email}</span>
                      <span className="text-[10px] text-slate-400">{new Date(r.suppressedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invalid domain list */}
            {delivResults.invalid.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-amber-500 uppercase tracking-wider">Bad Domain — no MX records</p>
                <div className="max-h-32 overflow-y-auto rounded-xl border border-amber-500/20 divide-y divide-amber-500/10">
                  {delivResults.invalid.map(r => (
                    <div key={r.id} className="flex items-center justify-between px-4 py-2 text-sm bg-amber-500/5">
                      <span className="text-slate-800 dark:text-white font-medium truncate max-w-[55%]">{r.email}</span>
                      <span className="text-[10px] text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded font-mono">{r.domain}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {totalProblematic === 0 && (
              <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                <p className="text-sm text-slate-700 dark:text-slate-200">All checked emails look clean — no issues found.</p>
              </div>
            )}

            {delivResults.unknown.length > 0 && (
              <p className="text-xs text-amber-500 dark:text-amber-400">
                {delivResults.unknown.length} email{delivResults.unknown.length !== 1 ? 's' : ''} had DNS timeouts — will be marked Invalid on apply.
              </p>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-slate-200 dark:border-white/5">
              <button onClick={() => { setIsDelivModalOpen(false); setDelivResults(null); }} className="btn-secondary">
                Close
              </button>
              {totalProblematic > 0 && (
                <button onClick={handleApplyDelivResults} disabled={applyingResults} className="btn-primary flex items-center gap-2">
                  {applyingResults
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <ShieldCheck size={15} />
                  }
                  Apply Results ({totalProblematic})
                </button>
              )}
            </div>
          </div>
          );
        })()}
      </Modal>
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        onConfirm={() => { confirmDialog.onConfirm(); closeConfirm(); }}
        onCancel={closeConfirm}
      />
    </div>
  );
};
export default Recipients;
